import { ButtonContainer, IDataTableColumn, ToolTipButton } from "nsitools-react";
import * as React from "react";

import { DashboardApi, DashboardCctScreeningDto, DashboardCctScreeningSearch } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { SearchTablePage, ViewButton } from "../../../components";
import { useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";
import { Button } from "@blueprintjs/core";
import styled from "styled-components";

const VerticalButtonContainer = styled.div`
  display: flex;
  width: 200px;
  gap: 0.5rem;
`;

export enum CctScreeningType {
  Basic = "Basic",
  Avertissement = "Avertissement",
  Decision = "Decision"
}

export interface IDashboardCctScreeningProps {
}

export const DashboardCctScreening: React.FunctionComponent<IDashboardCctScreeningProps> = props => {
  const { t } = useTl();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();

  const searchFunction = React.useCallback(
    (sObj?: DashboardCctScreeningSearch) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchDashboardCctScreening({ DashboardCctScreeningSearch: sObj });
    },
    [apiFactory]
  );

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { openTabThenSendMessage, onClose } = useTabMessage(identifier);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { dispatchEvent } = useEventsContext();

  const createSuivi = React.useCallback((row: DashboardCctScreeningDto) => {
    setRowLoading(row.idapprenant);
    openTabThenSendMessage(`#${ERoutes.apprenant}/${row.idapprenant}/suiviApprenant/0/edit/hope`, {
      ...row,
      dateAvertissementScreening: null
    }, [{
      key: "cctScreeningType",
      value: CctScreeningType.Basic.toString()
    }]);
  }, [openTabThenSendMessage]);

  const createSuivi75 = React.useCallback((row: DashboardCctScreeningDto) => {
    setRowLoading(row.idapprenant);
    openTabThenSendMessage(`#${ERoutes.apprenant}/${row.idapprenant}/suiviApprenant/0/edit/hope`, {
      ...row,
      dateAvertissementScreening: null
    }, [{
      key: "cctScreeningType",
      value: CctScreeningType.Avertissement.toString()
    }]);
  }, [openTabThenSendMessage]);

  const createSuivi90 = React.useCallback((row: DashboardCctScreeningDto) => {
    setRowLoading(row.idapprenant);
    openTabThenSendMessage(`#${ERoutes.apprenant}/${row.idapprenant}/suiviApprenant/0/edit/hope`, {
      ...row,
      dateAvertissementScreening: null
    }, [{
      key: "cctScreeningType",
      value: CctScreeningType.Decision.toString()
    }]);
  }, [openTabThenSendMessage]);

  onClose(() => {
    setRowLoading(null);
    dispatchEvent("SEARCH_TABLE_REFRESH");
  });

  const { showDialogPromise } = useDialog();
  const masquerLigne = React.useCallback(
    async (idapprenant: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerLigneConfirmTitle),
        message: t(ETLCodes.MasquerLigneConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idapprenant);
        await api.dashboardMasquerCctScreening({
          idapprenant: idapprenant
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Inscriptions");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DashboardCctScreeningDto) => (
          <ButtonContainer>
            {!row.masquee &&
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerLigne)}
                minimal
                icon="cross"
                onClick={() => masquerLigne(row.idapprenant)}
                loading={rowLoading === row.idapprenant}
                disabled={!!rowLoading && rowLoading !== row.idapprenant}
              />}
            <ViewButton
              minimal={true}
              onClick={() => window.open(`#${ERoutes.apprenant}/${row.idapprenant}/suiviApprenant/view`)}
            />
            <ToolTipButton
              tooltipPosition="bottom"
              tooltip={t(ETLCodes.CreateSuivi)}
              minimal
              icon="align-justify"
              onClick={() => createSuivi(row)}
              loading={rowLoading === row.idapprenant}
              disabled={!!rowLoading && rowLoading !== row.idapprenant}
            />
          </ButtonContainer>
        )
      },
      {
        computed: true,
        fieldName: "envois",
        autoFitContent: true,
        render: (row: DashboardCctScreeningDto) => (
          <VerticalButtonContainer>
            {!row.dateAvertissementScreening && <Button
              text={t(ETLCodes.Send75)}
              onClick={() => createSuivi75(row)}
              loading={rowLoading === row.idapprenant}
              disabled={!!rowLoading && rowLoading !== row.idapprenant}
              minimal
              outlined
              icon="send-message"
            />}
            <Button
              text={t(ETLCodes.Send90)}
              onClick={() => createSuivi90(row)}
              loading={rowLoading === row.idapprenant}
              disabled={!!rowLoading && rowLoading !== row.idapprenant}
              minimal
              outlined
              icon="send-message"
            />
          </VerticalButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Concerne),
        fieldName: "concerne"
      },
      {
        header: () => t(ETLCodes.AreCount),
        fieldName: "nbJoursAre"
      },
      {
        header: () => t(ETLCodes.AvertissementEnvoye75Jours),
        fieldName: "dateAvertissementScreening"
      },
      {
        header: () => t(ETLCodes.DecisionEnvoyee90Jours),
        fieldName: "dateDecisionScreening"
      }
    ],
    [createSuivi, createSuivi75, createSuivi90, masquerLigne, rowLoading, t]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <SearchTablePage
      withCard={false}
      columns={columns}
      searchFunction={searchFunction}
      onAbort={onAbort}
    />
  );
};
